import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Button, Dropdown } from 'antd';
import { API } from '@services/api.service';
import { ROLES_PERMISSIONS } from '@/constants';
import { useEnforceAccessControl, useNotify } from '@/hooks';
import { cn } from '@/libs/utils';

export default function DownloadDocuments({
  className,
  documentId,
  isFile,
  record,
}) {
  const { t } = useTranslation(['forms', 'messages']);
  const notification = useNotify();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (payload) => {
      return await API.documents.downloadDocument(payload);
    },
    onError: (error) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: error.message,
      });
    },
  });

  const onSubmit = async ({ action }) => {
    if (isFile) {
      const fileKey = record?.file?.key;
      const originalName = record?.file?.originalName || 'file';
      const mimeType = record?.file?.type || '';

      if (!fileKey) {
        return notification.error({
          message: t('error', { ns: 'messages' }),
          description: t('missingFileKey', { ns: 'messages' }),
        });
      }

      try {
        const res = await API.files.downloadFile({ key: fileKey });

        const previewableTypes = [
          'application/pdf',
          'image/jpeg',
          'image/png',
          'text/plain',
          'text/html',
        ];

        const link = document.createElement('a');
        link.href = res.url;
        link.download = originalName;

        if (previewableTypes.includes(mimeType)) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        notification.error({
          message: t('error', { ns: 'messages' }),
          description: error.message,
        });
      }
    } else {
      const payload = {
        ...action,
        documentId,
        returnDoc: true,
      };

      await mutateAsync(payload);
    }
  };

  const fileLabel = (
    <Button
      type="default"
      className="btn-default hover:!bg- m-0 flex h-max gap-2 rounded-none !bg-transparent px-2 py-1 hover:!bg-transparent"
      onClick={() => onSubmit({ action: { type: 'file' } })}
    >
      <img
        alt="File"
        src={`/assets/images/file.png`}
        className="block h-5 w-5 shadow-xl"
      />

      <h4>File</h4>
    </Button>
  );

  const pdfLabel = (
    <Button
      type="default"
      className="btn-default hover:!bg- m-0 flex h-max gap-2 rounded-none !bg-transparent px-2 py-1 hover:!bg-transparent"
      onClick={() => onSubmit({ action: { type: 'pdf' } })}
    >
      <img
        alt="PDF"
        src={`/assets/images/pdf.png`}
        className="block h-5 w-5 shadow-xl"
      />

      <h4>PDF</h4>
    </Button>
  );

  const docxLabel = (
    <Button
      type="default"
      className="btn-default hover:!bg- m-0 flex h-max gap-2 rounded-none !bg-transparent px-2 py-1 hover:!bg-transparent"
      onClick={() => onSubmit({ action: { type: 'doc' } })}
    >
      <img
        alt="DOCX"
        src={`/assets/images/docx.png`}
        className="block h-5 w-5 shadow-xl"
      />

      <h4>DOCX</h4>
    </Button>
  );

  const isMenuDisabled = useEnforceAccessControl(
    ROLES_PERMISSIONS.TIME_SHEET.ONLY_VIEW
  );

  const items = isFile
    ? [{ key: 'file', label: fileLabel }]
    : [
        { key: 'pdf', label: pdfLabel },
        { key: 'docx', label: docxLabel, disabled: isMenuDisabled },
      ];

  return (
    <Dropdown
      arrow
      menu={{ items }}
      trigger={['click']}
      placement="topRight"
      overlayClassName="custom-dropdown"
    >
      <Button
        type="button"
        loading={isPending}
        className={cn(
          'btn-default h-7 w-7 rounded bg-accent p-0 text-white hover:!bg-accent hover:!text-white',
          className
        )}
        icon={<DownloadOutlined className="text-base" />}
      >
        <span className="sr-only">{t('download', { ns: 'forms' })}</span>
      </Button>
    </Dropdown>
  );
}
